/// <reference path="../../../types/axios-fix.d.ts" />
import { BaseService, httpClientService, authInterceptorService, errorHandlerService, cacheService } from '../core';

/**
 * Permission interface
 */
export interface Permission {
  id: string;
  name: string;
  description?: string;
  resource: string;
  action: string;
  scope?: 'system' | 'organization' | 'team';
  created_at?: string;
  updated_at?: string;
}

/**
 * Permission Check Parameters
 */
export interface PermissionCheckParams {
  permissions: string | string[];
  mode?: 'all' | 'any';
  scope?: {
    organizationId?: string;
    teamId?: string;
  };
}

/**
 * Batch Permission Check Parameters
 */
export interface BatchPermissionCheckParams {
  checks: Array<{
    permission: string;
    context?: {
      type: 'organization' | 'team';
      id: string;
    };
  }>;
}

/**
 * Permission Service
 * 
 * Handles permission-related operations including:
 * - Fetching all permissions
 * - Getting user permissions
 * - Checking specific permissions
 * - Batch permission checks
 * 
 * Extracted from the monolithic API service.
 */
export class PermissionService extends BaseService {
  constructor() {
    super(httpClientService, authInterceptorService, errorHandlerService, cacheService);
  }

  /**
   * Get all available permissions
   */
  async getPermissions(): Promise<Permission[]> {
    try {
      console.log('Fetching permissions');
      
      // Use cached request with extended timeout for this critical operation
      const permissions = await this.getCached<Permission[]>(
        '/rbac/permissions',
        undefined,
        { timeout: 60000 } // 60 seconds timeout
      );
      
      console.log(`Retrieved ${permissions?.length || 0} permissions`);
      return permissions || [];
      
    } catch (error) {
      console.error('Error fetching permissions:', error);
      
      // Handle circuit breaker errors with retry logic
      const err = error as any;
      if (err.code === 'PERMISSION_SERVICE_UNAVAILABLE' || 
          err.code === 'CIRCUIT_OPEN' || 
          err.status === 503) {
        console.warn('Permission service unavailable (circuit breaker open), using empty permissions and scheduling retry');
        
        // Schedule a retry in the background
        setTimeout(() => {
          console.log('Retrying permission fetch after circuit breaker error');
          this.getPermissions()
            .then(() => console.log('Permission retry succeeded'))
            .catch(retryError => console.error('Permission retry failed:', retryError));
        }, 10000); // Try again in 10 seconds
        
        // Return empty array to prevent UI failures
        return [];
      }
      
      // For any other error, return empty array to prevent UI crashes
      console.error('Permission fetch failed, returning empty array');
      return [];
    }
  }

  /**
   * Get current user's permissions with optional scope
   * Note: API only supports getting the authenticated user's own permissions
   */
  async getUserPermissions(userId?: string, scope?: {
    organizationId?: string;
    teamId?: string;
  }): Promise<{ permissions: Permission[] }> {
    try {
      // Helper function to validate MongoDB ObjectId
      const isValidObjectId = (id: string): boolean => {
        return /^[0-9a-fA-F]{24}$/.test(id);
      };
      
      // Build query params for API call
      const params: Record<string, string> = {};
      
      if (scope?.organizationId) {
        // Validate organizationId before adding to params
        if (!isValidObjectId(scope.organizationId)) {
          console.warn(`Invalid organizationId format: ${scope.organizationId}`);
          // Return empty permissions for invalid ID instead of making a request that will fail
          return { permissions: [] };
        }
        params.context_type = 'organization';
        params.context_id = scope.organizationId;
      } else if (scope?.teamId) {
        // Validate teamId before adding to params
        if (!isValidObjectId(scope.teamId)) {
          console.warn(`Invalid teamId format: ${scope.teamId}`);
          // Return empty permissions for invalid ID instead of making a request that will fail
          return { permissions: [] };
        }
        params.context_type = 'team';
        params.context_id = scope.teamId;
      }
      
      // API only supports /users/me/permissions endpoint
      if (userId && userId !== 'me') {
        console.warn('getUserPermissions: API only supports current user permissions, ignoring userId parameter');
      }
      const endpoint = '/users/me/permissions';
      
      const response = await this.getCached<any>(
        endpoint,
        params,
        { timeout: 10000 }
      );
      
      // Handle nested data structure from Gateway V2
      const permissions = response?.data?.permissions || response?.permissions || [];
      
      console.log(`Retrieved ${permissions?.length || 0} user permissions`);
      return { permissions: permissions || [] };
      
    } catch (error) {
      console.error('Error fetching user permissions:', error);
      return { permissions: [] };
    }
  }

  /**
   * Check if user has specific permission(s)
   */
  async checkPermission(params: PermissionCheckParams): Promise<boolean> {
    try {
      console.log('Checking permission:', params);
      
      const permissions = Array.isArray(params.permissions) 
        ? params.permissions 
        : [params.permissions];
      
      const requestBody = {
        permissions,
        mode: params.mode || 'all',
        context: params.scope ? {
          type: params.scope.organizationId ? 'organization' : 'team',
          id: params.scope.organizationId || params.scope.teamId
        } : undefined
      };
      
      const response = await this.post<{ has_permission: boolean }>(
        '/rbac/check-permission',
        requestBody
      );
      
      console.log('Permission check result:', response.has_permission);
      return response.has_permission || false;
      
    } catch (error) {
      console.error('Error checking permission:', error);
      return false;
    }
  }

  /**
   * Check multiple permissions in a single request (batch check)
   */
  async checkPermissionsBatch(params: BatchPermissionCheckParams): Promise<Record<string, boolean>> {
    try {
      console.log('Checking permissions batch:', params);
      
      const response = await this.post<Record<string, boolean>>(
        '/rbac/check-permissions-batch',
        params
      );
      
      console.log('Batch permission check results:', response);
      return response || {};
      
    } catch (error) {
      console.error('Error checking permissions batch:', error);
      
      // Return all permissions as false on error
      const result: Record<string, boolean> = {};
      params.checks.forEach(check => {
        result[check.permission] = false;
      });
      return result;
    }
  }

  /**
   * Get permissions for a specific resource
   */
  async getResourcePermissions(resource: string, scope?: {
    organizationId?: string;
    teamId?: string;
  }): Promise<Permission[]> {
    try {
      const params: Record<string, string> = { resource };
      
      if (scope?.organizationId) {
        params.organization_id = scope.organizationId;
      }
      if (scope?.teamId) {
        params.team_id = scope.teamId;
      }
      
      const permissions = await this.getCached<Permission[]>(
        '/rbac/resource-permissions',
        params
      );
      
      return permissions || [];
      
    } catch (error) {
      console.error('Error fetching resource permissions:', error);
      return [];
    }
  }

  /**
   * Get all permissions for the current user's context
   */
  async getCurrentUserPermissions(): Promise<Permission[]> {
    try {
      const response = await this.getUserPermissions();
      return response.permissions;
    } catch (error) {
      console.error('Error fetching current user permissions:', error);
      return [];
    }
  }

  /**
   * Check if current user has a specific permission
   */
  async currentUserHasPermission(permission: string, scope?: {
    organizationId?: string;
    teamId?: string;
  }): Promise<boolean> {
    return this.checkPermission({
      permissions: permission,
      scope
    });
  }

  /**
   * Check if current user has all of the specified permissions
   */
  async currentUserHasAllPermissions(permissions: string[], scope?: {
    organizationId?: string;
    teamId?: string;
  }): Promise<boolean> {
    return this.checkPermission({
      permissions,
      mode: 'all',
      scope
    });
  }

  /**
   * Check if current user has any of the specified permissions
   */
  async currentUserHasAnyPermission(permissions: string[], scope?: {
    organizationId?: string;
    teamId?: string;
  }): Promise<boolean> {
    return this.checkPermission({
      permissions,
      mode: 'any',
      scope
    });
  }
}

// Export singleton instance
export const permissionService = new PermissionService();